/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { take } from 'rxjs';
import { SettingsService } from '../settings/settings.service';
import { NGXLogger } from 'ngx-logger';
import { environment } from 'src/environments/environment';
import { SettingsModel } from '../settings/settings.model';
import rg4js from 'raygun4js';
import versionData from '../../../environments/version.json';

const SENSITIVE_KEYS = [
  'authorization',
  'cookie',
  'set-cookie',
  'password',
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'code',
  'code_verifier',
  'client_secret',
  'state',
  'x-api-key',
  'x-avmin-api-key',
  'x-avmin-desktop-api-key',
  'x-avmin-exchange-token',
];

@Injectable({ providedIn: 'root' })
export class RaygunErrorHandler implements ErrorHandler {
  private readonly _logger = inject(NGXLogger);
  private readonly _reportedErrors = new Set<string>();
  private readonly _settings = inject(SettingsService);

  constructor() {
    this._logger.info('[RaygunErrorHandler][Version]', versionData.version);
    this._init();
  }

  private _init() {
    this._settings
      .getSettings()
      .pipe(take(1))
      .subscribe((settings: SettingsModel) => {
        rg4js('apiKey', settings.raygunKey);
        rg4js('setVersion', versionData.version);
        rg4js('filterSensitiveData', SENSITIVE_KEYS);
        rg4js('options', {
          captureUnhandledRejections: false,
          ignore3rdPartyErrors: true,
        });

        if (environment.production) {
          rg4js('enableCrashReporting', true);
          this._logger.debug('[RaygunErrorHandler] enabled');
        }
      });
  }

  private _getSignature(error: any): string {
    if (!error) return 'unknown';

    return [
      error.name,
      error.message,
      error.stack?.split('\n')[0], // first stack line is usually enough
    ].join('|');
  }

  private _scrub(value: any, depth = 0): any {
    if (value === null || typeof value !== 'object') {
      return value;
    }
    if (depth > 5) {
      return '[truncated]';
    }
    if (value instanceof HttpHeaders) {
      return '[headers removed]';
    }
    if (Array.isArray(value)) {
      return value.map((v) => this._scrub(v, depth + 1));
    }

    const result: Record<string, any> = {};
    for (const key of Object.keys(value)) {
      result[key] = SENSITIVE_KEYS.includes(key.toLowerCase()) ? '[redacted]' : this._scrub(value[key], depth + 1);
    }
    return result;
  }

  handleError(error: any): void {
    this._logger.error(error);

    if (!environment.production) {
      return;
    }

    const ignoredStatusCodes = [400, 403, 500, 503];
    if (ignoredStatusCodes.includes(error?.status)) {
      return;
    }

    const signature = this._getSignature(error);
    if (this._reportedErrors.has(signature)) {
      return;
    }

    this._logger.debug(`[RaygunErrorHandler][handleError] signature: ${signature}`);
    this._reportedErrors.add(signature);

    if (error instanceof Error) {
      rg4js('send', {
        error: error,
      });
    } else if (error instanceof String) {
      rg4js('send', {
        error: new Error(error.toString()),
      });
    } else {
      rg4js('send', {
        error: new Error('Unknown error, see custom data for details'),
        customData: [{ originalError: this._scrub(error) }],
      });
    }
  }
}
